// game/shared/src/terrain-smooth.ts
//
// Edge-aware height smoothing shared by the VISUAL terrain (client terrain.ts) and the
// NAVMESH bake (server navmesh.ts) so both derive from the SAME ramp-aware surface —
// they can't drift. A multi-pass 3×3 gaussian (1-2-1 / 4) blur that NEVER averages
// across a hard plateau↔plateau cliff lip (those stay crisp / steep → a wall in the
// render AND a non-walkable step in recast), but DOES blend ramps and slopes (so an
// authored ramp's per-cell steps fall below recast's walkableClimb → it stays a
// walkable slope, matching the KTS pathing layer).
//
// The client runs few passes (just to de-facet ramps visually); the navmesh runs more
// (enough to drop ramp steps under walkableClimb). Hard cliffs are preserved either way.

import { CLIFF_PLATEAU_LEVELS } from "./cliff-edges";

const isPlateau = (lvl: number): boolean => CLIFF_PLATEAU_LEVELS.has(lvl);
// Boundaries the blur must NOT cross, or it averages real terrain into a void / across a
// wall and drags plateaus down:
//  - hard cliff lip: both sides plateaus at DIFFERENT levels (a real rock wall); and
//  - any boundary touching OFF-MAP / chasm (level 0): height there is ~0, so smoothing a
//    plateau into it sinks the plateau edge into the pit (this sank chasm-pocked plateaus
//    until they fragmented out of the navmesh).
// Ramp cells (intermediate) and ramp↔plateau junctions ARE crossed, so ramps stay smooth.
const blocksBlur = (a: number, b: number): boolean =>
  a === 0 || b === 0 || (a !== b && isPlateau(a) && isPlateau(b));

/**
 * Edge-aware multi-pass blur. `heights` is a vertDim×vertDim grid (vertDim = cellDim+1);
 * `cliffLevels` is the cellDim×cellDim cliff-level grid. Returns a NEW array; the input
 * is never mutated. Cells across a hard cliff edge are excluded from each cell's blur, so
 * the dramatic step survives any number of passes.
 */
export function smoothHeightsEdgeAware(
  heights: Float32Array,
  cliffLevels: Uint16Array,
  passes: number,
): Float32Array {
  const vertDim = Math.round(Math.sqrt(heights.length));
  const cellDim = Math.round(Math.sqrt(cliffLevels.length));
  const levelAtVertex = (row: number, col: number): number => {
    const r = row >= cellDim ? cellDim - 1 : row;
    const c = col >= cellDim ? cellDim - 1 : col;
    return cliffLevels[r * cellDim + c]!;
  };

  let cur: Float32Array = heights;
  let scratch: Float32Array = new Float32Array(heights.length);
  for (let pass = 0; pass < passes; pass++) {
    for (let row = 0; row < vertDim; row++) {
      for (let col = 0; col < vertDim; col++) {
        const centerLevel = levelAtVertex(row, col);
        let sum = 0;
        let weight = 0;
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            const r = row + dr;
            const c = col + dc;
            if (r < 0 || r >= vertDim || c < 0 || c >= vertDim) continue;
            if (blocksBlur(centerLevel, levelAtVertex(r, c))) continue;
            const w = (dr === 0 ? 2 : 1) * (dc === 0 ? 2 : 1);
            sum += cur[r * vertDim + c]! * w;
            weight += w;
          }
        }
        scratch[row * vertDim + col] = weight > 0 ? sum / weight : cur[row * vertDim + col]!;
      }
    }
    if (pass === 0) {
      // Pass 0 read the caller's array and wrote a fresh buffer; keep input pristine and
      // ping-pong between two scratch buffers from here on.
      cur = scratch;
      scratch = new Float32Array(heights.length);
    } else {
      const t = cur; cur = scratch; scratch = t;
    }
  }
  return cur;
}
